import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs';
import { SidebarService } from '../../service/sidebarservice.service';
import {
  TransactionAccessKey,
  TransactionAccessService
} from '../../shared/services/transaction-access.service';


type SidebarLink = {
  label: string; icon: string; path: string; type?: 'link'; accessKey: TransactionAccessKey;
};
type SidebarGroup = {
  label: string; icon: string; type: 'group'; children: SidebarItem[];
};
type SidebarItem = SidebarLink | SidebarGroup;

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss'
})
export class Sidebar {
  sidebar = inject(SidebarService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private transactionAccess = inject(TransactionAccessService);

  currentUrl = signal(this.router.url);
  openGroups = signal<Record<string, boolean>>({});

  collapsed = this.sidebar.collapsed;

  menu = this.filterAuthorizedMenu(this.ordenarPorLabel<SidebarItem>([
    {
      label: 'Levantamiento de Fuentes',
      icon: 'bi-building-up',
      type: 'group',
      children: this.ordenarPorLabel([
        { label: 'Levantar Fuente', icon: 'bi-plus-circle', path: '/source-fieldwork', accessKey: 'sourceFieldwork' }
      ])
    },

    {
      label: 'Boletas',
      icon: 'bi-receipt',
      type: 'group',
      children: this.ordenarPorLabel([
        { label: 'Consultar boletas', icon: 'bi-search', path: '/consulta-boletas', accessKey: 'ballotQuery' },
        { label: 'Captura de boletas', icon: 'bi-clipboard-check', path: '/ballot-capture', accessKey: 'ballotCapture' },
        { label: 'Editar boletas', icon: 'bi-pencil-square', path: '/ballot-editing', accessKey: 'ballotEditing' },
        { label: 'Revisión de boletas', icon: 'bi-clipboard2-check', path: '/revision-boletas', accessKey: 'ballotReview' }
      ])
    },

    {
      label: 'Reportes',
      icon: 'bi-bar-chart-line',
      type: 'group',
      children: this.ordenarPorLabel([
        { label: 'Reporte de índices', icon: 'bi-file-earmark-bar-graph', path: '/reportes/indices', accessKey: 'exportData' },
        { label: 'Reporte de incidencias', icon: 'bi-graph-up-arrow', path: '/reportes/incidencias', accessKey: 'exportData' },
        { label: 'Reporte de precios atípicos', icon: 'bi-exclamation-triangle', path: '/reportes/precios-atipicos', accessKey: 'exportData' },
        { label: 'Reporte de variación', icon: 'bi-activity', path: '/reportes/variacion', accessKey: 'exportData' },
        { label: 'Exportar Datos', icon: 'bi-file-earmark-plus', path: '/export-data', accessKey: 'exportData' }
      ])
    },

    {
      label: 'Cálculos',
      icon: 'bi-calculator',
      type: 'group',
      children: this.ordenarPorLabel([
        { label: 'Ejecutar cálculo', icon: 'bi-play-circle', path: '/calculos/ejecutar', accessKey: 'calculationExecution' },
        { label: 'Visualizar cálculo', icon: 'bi-eye', path: '/calculos', accessKey: 'calculations' },
        {
          label: 'Generar boletas',
          icon: 'bi-file-earmark-plus',
          path: '/calculos/generar-boletas',
          accessKey: 'calculations'
        }
      ])
    },

    {
      label: 'Administración',
      icon: 'bi-gear',
      type: 'group',
      children: this.ordenarPorLabel([
        { label: 'Bitácora', icon: 'bi-journal-text', path: '/bitacora', accessKey: 'audit' }

      ])
    },

    {
      label: 'Mantenimientos',
      icon: 'bi-sliders',
      type: 'group',
      children: this.ordenarPorLabel([
        { label: 'Actividades de Empresa', icon: 'bi-briefcase', path: '/actividades-empresa', accessKey: 'businessActivities' },
        { label: 'Características', icon: 'bi-ui-checks-grid', path: '/caracteristicas', accessKey: 'characteristics' },
        { label: 'Valores de Características', icon: 'bi-list-ul', path: '/caracteristicas/valores', accessKey: 'characteristicValues' },
        { label: 'Tamaños de Empresa', icon: 'bi-arrows-angle-expand', path: '/tamanos-empresa', accessKey: 'companySizes' },
        { label: 'Unidades de Medida', icon: 'bi-rulers', path: '/unidades-medida', accessKey: 'measurementUnits' },
        { label: 'Periodicidades de Visita', icon: 'bi-calendar-range', path: '/periodicidades-visita', accessKey: 'visitFrequencies' },
        { label: 'Razones NT', icon: 'bi-chat-square-text', path: '/razones-nt', accessKey: 'nonResponseReasons' },
        { label: 'Razones de Variación', icon: 'bi-graph-up-arrow', path: '/razones-variacion', accessKey: 'variationReasons' },
        { label: 'Artículos', icon: 'bi-box-seam', type: 'group', children: this.ordenarPorLabel([
          { label: 'Agregar', icon: 'bi-plus-circle', path: '/add-article', accessKey: 'articles' },
          { label: 'Consultar', icon: 'bi-search', path: '/search-article', accessKey: 'articles' }
        ]) },
        { label: 'Fuentes', icon: 'bi-building', type: 'group', children: this.ordenarPorLabel([
          { label: 'Agregar', icon: 'bi-plus-circle', path: '/add-source', accessKey: 'sources' },
          { label: 'Consultar', icon: 'bi-search', path: '/search-source', accessKey: 'sources' }
        ]) },
        { label: 'Productos', icon: 'bi-cart4', type: 'group', children: this.ordenarPorLabel([
          { label: 'Agregar', icon: 'bi-plus-circle', path: '/add-product', accessKey: 'products' },
          { label: 'Consultar', icon: 'bi-search', path: '/search-product', accessKey: 'products' }
        ]) },
        { label: 'Variedades', icon: 'bi-tags', type: 'group', children: this.ordenarPorLabel([
          { label: 'Agregar', icon: 'bi-plus-circle', path: '/add-variety', accessKey: 'varieties' },
          { label: 'Consultar', icon: 'bi-search', path: '/search-variety', accessKey: 'varieties' }
        ]) },
        { label: 'Relación Usuario – Transacción', icon: 'bi-person-check', path: '/transacciones-usuarios', accessKey: 'userTransactions' },
        { label: 'Transacciones', icon: 'bi-list-check', path: '/transacciones', accessKey: 'transactions' },
        { label: 'Usuarios', icon: 'bi-people', path: '/usuarios', accessKey: 'users' },
      ])
    }

  ]));

  constructor() {
    this.syncGroupsWithRoute(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.syncGroupsWithRoute(event.urlAfterRedirects);
      });
  }

  toggleCollapsed() {
    this.sidebar.toggleCollapsed();
  }

  toggleGroup(label: string) {
    if (this.collapsed()) {
      this.sidebar.setCollapsed(false);
    }

    this.openGroups.update(s => ({ ...s, [label]: !s[label] }));
  }

  isGroupOpen(label: string): boolean {
    return !!this.openGroups()[label];
  }

  isItemActive(path: string): boolean {
    return this.isSameRoute(path, this.currentUrl());
  }

  isGroupActive(item: SidebarGroup): boolean {
    return item.children.some(child =>
      child.type === 'group' ? this.isGroupActive(child) : this.isItemActive(child.path)
    );
  }

  handleNavigation(): void {
    this.sidebar.closeOnMobile();
  }

  private ordenarPorLabel<T extends { label: string }>(items: T[]): T[] {
    return [...items].sort((a, b) =>
      a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })
    );
  }

  private filterAuthorizedMenu(items: SidebarItem[]): SidebarItem[] {
    return items.reduce<SidebarItem[]>((authorizedItems, item) => {
      if (item.type !== 'group') {
        if (this.transactionAccess.hasAccess(item.accessKey, item.path)) {
          authorizedItems.push(item);
        }
        return authorizedItems;
      }

      const children = this.filterAuthorizedMenu(item.children);
      if (children.length) {
        authorizedItems.push({ ...item, children });
      }
      return authorizedItems;
    }, []);
  }

  private syncGroupsWithRoute(url: string): void {
    this.openGroups.update((currentState) => {
      const nextState = { ...currentState };

      for (const item of this.menu) {
        if (item.type !== 'group') continue;

        this.openMatchingGroups(item, url, nextState);
      }

      return nextState;
    });
  }

  private openMatchingGroups(group: SidebarGroup, url: string, state: Record<string, boolean>): boolean {
    const containsRoute = group.children.some(child =>
      child.type === 'group'
        ? this.openMatchingGroups(child, url, state)
        : this.isSameRoute(child.path, url)
    );
    if (containsRoute) state[group.label] = true;
    return containsRoute;
  }

  private isSameRoute(path: string, url: string): boolean {
    const normalizedPath = path.split('?')[0];
    const normalizedUrl = url.split('?')[0];

    return normalizedPath === normalizedUrl;
  }
}
